import React from "react";
import {
  VERTEX360_COPILOT_SIDEBAR_WIDTH_EXPANDED,
} from "../constants/vertex360CopilotWorkspaceConstants";

const Pulse = ({ className = "", style }) => (
  <div
    className={`animate-pulse rounded-md bg-gray-200/80 ${className}`}
    style={style}
    aria-hidden="true"
  />
);

export const SkeletonConversationRow = ({ active = false, short = false }) => (
  <div
    className={`flex items-center gap-2 rounded-lg px-2 py-2 ${
      active ? "border border-[#C7D7F5] bg-[#ECF0FD]" : ""
    }`}
  >
    <Pulse className="h-3.5 w-3.5 shrink-0 rounded-full" />
    <Pulse className={short ? "h-3.5 w-28" : "h-3.5 w-40"} />
  </div>
);

/** Sidebar conversation list placeholder while Laravel history loads. */
export const Vertex360CopilotSidebarConversationsSkeleton = ({
  rows = 6,
  showActive = true,
} = {}) => {
  const count = Math.max(1, Number(rows) || 6);
  return (
    <div className="space-y-1" aria-hidden="true">
      {Array.from({ length: count }, (_, index) => (
        <SkeletonConversationRow
          key={`sidebar-skel-${index}`}
          active={showActive && index === 0}
          short={index % 2 === 1}
        />
      ))}
    </div>
  );
};

/** Chat message area placeholder while a persisted conversation's messages load. */
export const Vertex360CopilotChatMessagesSkeleton = () => (
  <div
    className="flex min-h-full flex-col justify-end space-y-6 py-2"
    aria-busy="true"
    aria-label="Loading conversation messages"
  >
    <div className="flex items-start justify-end gap-2.5">
      <div className="flex max-w-[72%] flex-col items-end gap-2">
        <Pulse className="h-10 w-48 rounded-2xl rounded-tr-md" />
        <Pulse className="h-3 w-16" />
      </div>
      <Pulse className="h-8 w-8 shrink-0 rounded-full" />
    </div>

    <div className="flex items-start gap-3">
      <Pulse className="h-8 w-8 shrink-0 rounded-full" />
      <div className="min-w-0 flex-1 space-y-2">
        <Pulse className="h-20 w-full max-w-md rounded-2xl rounded-tl-md" />
        <Pulse className="h-3 w-20" />
      </div>
    </div>

    <div className="flex items-start justify-end gap-2.5">
      <div className="flex max-w-[72%] flex-col items-end gap-2">
        <Pulse className="h-8 w-56 rounded-2xl rounded-tr-md" />
        <Pulse className="h-3 w-14" />
      </div>
      <Pulse className="h-8 w-8 shrink-0 rounded-full" />
    </div>

    <div className="flex items-start gap-3">
      <Pulse className="h-8 w-8 shrink-0 rounded-full" />
      <div className="min-w-0 flex-1 space-y-2">
        <Pulse className="h-24 w-full max-w-lg rounded-2xl rounded-tl-md" />
        <Pulse className="h-3 w-24" />
      </div>
    </div>
  </div>
);

const Vertex360CopilotWorkspaceSkeleton = () => (
  <div
    className="copilot-workspace flex min-h-0 flex-1 overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm"
    aria-label="Loading Vertex360 Copilot workspace"
    aria-busy="true"
  >
    <aside
      className="flex shrink-0 flex-col border-r border-slate-200/80 bg-[#FAFBFD]"
      style={{ width: VERTEX360_COPILOT_SIDEBAR_WIDTH_EXPANDED }}
    >
      <div className="flex items-center justify-between gap-2 border-b border-slate-200/70 px-3 py-3">
        <div className="flex min-w-0 items-center gap-2">
          <Pulse className="h-8 w-8 rounded-lg" />
          <Pulse className="h-3.5 w-20" />
        </div>
        <Pulse className="h-8 w-8 rounded-lg" />
      </div>

      <div className="space-y-2 px-2 py-3">
        <Pulse className="h-10 w-full rounded-lg" />
        <Pulse className="h-9 w-full rounded-lg" />
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden px-2 pb-3">
        <div>
          <Pulse className="mb-2 h-3 w-16" />
          <div className="space-y-1">
            <SkeletonConversationRow active />
            <SkeletonConversationRow short />
          </div>
        </div>

        <div className="min-h-0 flex-1">
          <Pulse className="mb-2 h-3 w-24" />
          <div className="space-y-1">
            <SkeletonConversationRow />
            <SkeletonConversationRow short />
            <SkeletonConversationRow />
            <SkeletonConversationRow short />
          </div>
        </div>
      </div>
    </aside>

    <main className="flex min-w-0 flex-1 flex-col bg-white">
      <div className="shrink-0 border-b border-slate-200/80 bg-white/95 px-5 py-3.5">
        <div className="flex items-center gap-3">
          <Pulse className="h-9 w-9 rounded-xl" />
          <div className="min-w-0 space-y-2">
            <Pulse className="h-4 w-28" />
            <Pulse className="h-3 w-40" />
          </div>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col justify-start overflow-hidden px-4 py-6">
        <div className="mx-auto mb-5 flex w-full max-w-xl flex-col items-center">
          <Pulse className="mb-4 h-12 w-12 rounded-xl" />
          <Pulse className="mb-3 h-5 w-36" />
          <Pulse className="h-3.5 w-72 max-w-full" />
        </div>

        <div className="mx-auto w-full max-w-2xl space-y-3">
          <div className="flex gap-2 overflow-hidden rounded-xl border border-[#C7D7F5]/70 bg-[#F8FAFF] p-1.5">
            <Pulse className="h-8 w-24 rounded-full" />
            <Pulse className="h-8 w-28 rounded-full" />
            <Pulse className="h-8 w-32 rounded-full" />
            <Pulse className="h-8 w-28 rounded-full" />
          </div>

          {[0, 1, 2, 3, 4].map((item) => (
            <div
              key={item}
              className="rounded-xl border border-slate-200/80 bg-white px-3.5 py-3"
            >
              <Pulse className="mb-2 h-3.5 w-48 max-w-full" />
              <Pulse className="h-3 w-full max-w-md" />
            </div>
          ))}
        </div>
      </div>

      <div className="shrink-0 border-t border-slate-200/80 bg-white px-4 py-4">
        <div className="rounded-2xl border border-slate-200/80 bg-[#F4F7FE] p-3">
          <Pulse className="h-8 w-full rounded-xl" />
        </div>
      </div>
    </main>
  </div>
);

export default Vertex360CopilotWorkspaceSkeleton;
